// Composes the conditional content inside the main editor section.
import React, { Suspense } from "react";
import { createPortal } from "react-dom";

import { TONES } from "../../../../src/shared/constants";
import Brainstorm from "../Brainstorm";
import Commander from "../Commander";
import { TiptapErrorBoundary } from "../TiptapErrorBoundary";
import EditorContainer from "./EditorContainer";
import Menu from "./Menu";
import { NewDocumentCTAs } from "./NewDocumentCTAs/index.js";
import RelatedWords from "./RelatedWords.js";
import TrashedItem from "./TrashedItem";

const MagicCursor = React.lazy(() => import("../MagicCursor"));

function EditorContentPanels({ relatedWordsProps, commandModeProps, menuProps, brainstormProps, editorDocumentProps, newDocumentCtaProps }) {
  const { menuComponentRef, subscription, ...menuRest } = menuProps;
  const { trashedItem, editorName, documentId, userId, projectId, editorEndRef, ...editorContainerProps } = editorDocumentProps;
  const {
    showRelatedWords,
    documentId: relatedWordsDocumentId,
    relatedWordsStore,
    insertRelatedWord,
    close: closeRelatedWords,
  } = relatedWordsProps;
  const {
    loadingDocument,
    commandModeState,
    wordCount,
    tiptapTextLength,
    subscription: commandModeSubscription,
    leftBarCollapsed,
    editorOptions: commandModeEditorOptions,
    commandModeInserting,
    setCommandModeState,
    handleCommandMode,
    editorSectionRef,
    caretPosition,
    magicCursorVisible,
    theme,
  } = commandModeProps;
  const {
    brainstormOpen,
    documentId: brainstormDocumentId,
    closeBrainstorm,
    seenTips,
    setSeenTips,
  } = brainstormProps;
  const {
    subscription: newDocumentSubscription,
    showNewDocumentCTAs,
    deleted,
    trashedItem: newDocumentTrashedItem,
    proseGeneratorActive,
    editorOptions: newDocumentEditorOptions,
    openWormhole,
    runDocumentPluginAction,
    documentPluginBridge,
  } = newDocumentCtaProps;
  const showCommander =
    !loadingDocument &&
    (commandModeState === "show" || commandModeState === "loading") &&
    wordCount === 0 &&
    tiptapTextLength === 0;

  return (
    <>
      {showRelatedWords && relatedWordsDocumentId && createPortal(
        <RelatedWords
          key="related"
          relatedWordsStore={relatedWordsStore}
          initialWord={showRelatedWords.word}
          initialParagraph={showRelatedWords.paragraph}
          insertRelatedWord={insertRelatedWord}
          range={showRelatedWords.range}
          relativeIndex={showRelatedWords.relativeIndex}
          close={closeRelatedWords}
          documentId={relatedWordsDocumentId}
        />,
        document.body
      )}
      {showCommander && (
        <Commander
          active={commandModeSubscription.hasAccessToFeatures}
          leftBarCollapsed={leftBarCollapsed}
          lineHeight={commandModeEditorOptions.lineHeight}
          fontSize={commandModeEditorOptions.fontSize}
          indented={commandModeEditorOptions.textIndent}
          isInserting={commandModeInserting}
          commandModeState={commandModeState}
          setCommandModeState={setCommandModeState}
          handleSubmit={handleCommandMode}
          innerRef={editorSectionRef}
        />
      )}
      {commandModeInserting && (
        <Suspense fallback={null}>
          <MagicCursor
            caretPosition={caretPosition}
            visible={magicCursorVisible}
            theme={theme}
            fontSize={commandModeEditorOptions.fontSize}
          />
        </Suspense>
      )}
      <Menu
        {...menuRest}
        ref={menuComponentRef}
        tones={TONES}
        hasAccessToFeatures={subscription.hasAccessToFeatures}
        subscription={subscription}
        hideBackButton={false}
        hideSettings={false}
      />
      {brainstormOpen && (
        <Brainstorm
          documentId={brainstormDocumentId}
          listId={typeof brainstormOpen === "string" ? brainstormOpen : null}
          closeBrainstorm={closeBrainstorm}
          seenTips={seenTips}
          setSeenTips={setSeenTips}
        />
      )}
      <div style={{ display: trashedItem ? "none" : "block" }}>
        <TiptapErrorBoundary
          editorName={editorName}
          documentId={documentId}
          userId={userId}
          projectId={projectId}
        >
          <EditorContainer
            {...editorContainerProps}
            documentId={documentId}
            editorEndRef={editorEndRef}
          />
        </TiptapErrorBoundary>
        <div
          id="editor-end"
          ref={editorEndRef}
        />
      </div>
      {trashedItem && <TrashedItem item={trashedItem} />}
      {newDocumentSubscription?.hasAccessToChapterGlueBeta && showNewDocumentCTAs && !deleted && !newDocumentTrashedItem && !proseGeneratorActive && (
        <NewDocumentCTAs
          editorOptions={newDocumentEditorOptions}
          openWormhole={openWormhole}
          runDocumentPluginAction={runDocumentPluginAction}
          documentPluginBridge={documentPluginBridge}
        />
      )}
    </>
  );
}

export default React.memo(EditorContentPanels);
